"use client"

import { useState, useEffect } from "react"
import {
  Col,
  Row,
  Table,
  Select,
  Spin,
  Modal,
  Button,
  Tooltip,
  Input,
  Breadcrumb,
  Switch,
  Card,
  Space,
  Tag,
  Divider,
  Typography,
  Empty,
} from "antd"
import {
  DownloadOutlined,
  HomeOutlined,
  EyeOutlined,
  FileTextOutlined,
  CalendarOutlined,
  UserOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import { toast } from "react-toastify"
import moment from "moment"
import styles from "./style.module.scss"
import { api } from "../../services/apiClient"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"

const { Option } = Select
const { Title, Text } = Typography
const { Search } = Input

const Consultas = () => {
  const [loading, setLoading] = useState(true)
  const [clientes, setClientes] = useState([])
  const [filteredClientes, setFilteredClientes] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState("")
  const [size, setSize] = useState("large")
  const [secondModalOpen, setSecondModalOpen] = useState(false)
  const [selectedCliente, setSelectedCliente] = useState(null)
  const [previewFileUrl, setPreviewFileUrl] = useState("")
  const [searchText, setSearchText] = useState("")
  const [filterEtapa, setFilterEtapa] = useState("all")
  const [filterPresencial, setFilterPresencial] = useState("all")

  // Auxiliar para formatar a data apenas no modal ou onde for necessário
  const formatIfValid = (dateStr) => {
    if (!dateStr) return ""
    const dateObj = moment(dateStr, "DD/MM/YYYY", true)
    return dateObj.isValid() ? dateObj.format("DD/MM/YYYY") : dateStr
  }

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const response = await api.get("/clientes")
        setClientes(response.data)
        setFilteredClientes(response.data)
        setLoading(false)
      } catch (error) {
        toast.error("Erro ao buscar clientes.")
        setLoading(false)
      }
    }
    fetchClientes()
  }, [])

  // Filtros
  useEffect(() => {
    let filtered = clientes

    // Filtro por texto
    if (searchText) {
      filtered = filtered.filter(
        (cliente) =>
          cliente.nomeCliente?.toLowerCase().includes(searchText.toLowerCase()) ||
          cliente.email?.toLowerCase().includes(searchText.toLowerCase()) ||
          cliente.telefone?.includes(searchText) ||
          cliente.cpfCnpj?.includes(searchText),
      )
    }

    // Filtro por etapa
    if (filterEtapa !== "all") {
      filtered = filtered.filter((cliente) => cliente.etapa === filterEtapa)
    }

    // Filtro por presencial
    if (filterPresencial !== "all") {
      const isPresencial = filterPresencial === "presencial"
      filtered = filtered.filter((cliente) => cliente.isPresencial === isPresencial)
    }

    setFilteredClientes(filtered)
  }, [clientes, searchText, filterEtapa, filterPresencial])

  const handleEtapaChange = async (value, record) => {
    try {
      const updatedClientes = clientes.map((cliente) =>
        cliente.id === record.id ? { ...cliente, etapa: value } : cliente,
      )
      setClientes(updatedClientes)
      await api.put(`/clientes/${record.id}`, { etapa: value })
      toast.success("Etapa atualizada com sucesso.")
    } catch (error) {
      toast.error("Erro ao atualizar etapa.")
    }
  }

  const handleInputDateChange = async (e, record) => {
    const typed = e.target.value

    const updatedClientes = clientes.map((cliente) =>
      cliente.id === record.id ? { ...cliente, dataConsulta: typed } : cliente,
    )
    setClientes(updatedClientes)

    if (!typed) {
      try {
        await api.put(`/clientes/${record.id}`, { dataConsulta: "" })
        toast.success("Data da consulta atualizada (removida) com sucesso.")
      } catch (error) {
        toast.error("Erro ao atualizar data da consulta.")
      }
      return
    }

    const dateObj = moment(typed, "DD/MM/YYYY", true)
    if (!dateObj.isValid()) {
      return
    }

    try {
      await api.put(`/clientes/${record.id}`, { dataConsulta: typed })
      toast.success("Data da consulta atualizada com sucesso.")
    } catch (error) {
      toast.error("Erro ao atualizar data da consulta.")
    }
  }

  const handlePresencialChange = async (checked, record) => {
    try {
      const updatedClientes = clientes.map((cliente) =>
        cliente.id === record.id ? { ...cliente, isPresencial: checked } : cliente,
      )
      setClientes(updatedClientes)
      await api.put(`/clientes/${record.id}`, { isPresencial: checked })
      toast.success("Status de atendimento presencial atualizado com sucesso.")
    } catch (error) {
      toast.error("Erro ao atualizar status de atendimento presencial.")
    }
  }

  const handlePreview = (url) => {
    window.open(url, "_blank")
  }

  const handleDownloadClick = (record) => {
    setSelectedCliente(record)
    setSecondModalOpen(true)
  }

  const generatePDF = () => {
    const modalContent = document.getElementById("second-modal-content")
    html2canvas(modalContent).then((canvas) => {
      const imgData = canvas.toDataURL("image/jpeg", 1.0)
      const pdf = new jsPDF("p", "mm", "a4")
      pdf.addImage(imgData, "JPEG", 10, 10, 190, 120)
      pdf.save(`cliente-${selectedCliente.nomeCliente}.pdf`)
    })
  }

  const clearFilters = () => {
    setSearchText("")
    setFilterEtapa("all")
    setFilterPresencial("all")
  }

  const getEtapaColor = (etapa) => {
    switch (etapa) {
      case "contato":
        return "blue"
      case "negociacao":
        return "orange"
      case "fechado":
        return "green"
      default:
        return "default"
    }
  }

  const getEtapaText = (etapa) => {
    switch (etapa) {
      case "contato":
        return "Contato"
      case "negociacao":
        return "Negociação"
      case "fechado":
        return "Fechado"
      default:
        return etapa
    }
  }

  const columns = [
    {
      title: "Cliente",
      dataIndex: "nomeCliente",
      key: "nomeCliente",
      width: 200,
      render: (text, record) => (
        <div className={styles.clientInfo}>
          <div className={styles.clientName}>{text}</div>
          <div className={styles.clientEmail}>{record.email}</div>
        </div>
      ),
    },
    {
      title: "Etapa",
      key: "etapa",
      dataIndex: "etapa",
      width: 150,
      render: (_, record) => (
        <Select
          value={record.etapa}
          style={{ width: "100%" }}
          onChange={(value) => handleEtapaChange(value, record)}
          className={styles.selectEtapa}
        >
          <Option value="contato">
            <Tag color="blue">Contato</Tag>
          </Option>
          <Option value="negociacao">
            <Tag color="orange">Negociação</Tag>
          </Option>
          <Option value="fechado">
            <Tag color="green">Fechado</Tag>
          </Option>
        </Select>
      ),
    },
    {
      title: "Atendimento",
      dataIndex: "isPresencial",
      key: "isPresencial",
      width: 120,
      render: (_, record) => (
        <div className={styles.presencialContainer}>
          <Switch
            checked={record.isPresencial}
            onChange={(checked) => handlePresencialChange(checked, record)}
            size="small"
          />
          <span className={styles.presencialText}>{record.isPresencial ? "Presencial" : "Online"}</span>
        </div>
      ),
    },
    {
      title: "Data da Consulta",
      dataIndex: "dataConsulta",
      key: "dataConsulta",
      width: 160,
      render: (_, record) => (
        <Input
          placeholder="DD/MM/YYYY"
          value={record.dataConsulta || ""}
          onChange={(e) => handleInputDateChange(e, record)}
          prefix={<CalendarOutlined />}
          className={styles.dateInput}
        />
      ),
    },
    {
      title: "Contato",
      key: "contato",
      width: 200,
      render: (_, record) => (
        <div className={styles.contactInfo}>
          <div className={styles.contactItem}>
            <PhoneOutlined />
            <span>{record.telefone}</span>
          </div>
          <div className={styles.contactItem}>
            <Text copyable={{ text: record.cpfCnpj }}>{record.cpfCnpj}</Text>
          </div>
        </div>
      ),
    },
    {
      title: "Endereço",
      dataIndex: "enderecoCompleto",
      key: "enderecoCompleto",
      width: 250,
      render: (_, record) => {
        const enderecoCompleto = `${record.logradouro}, ${record.numero}, ${record.bairro}, ${record.cidade}, ${record.estado}, ${record.cep}`
        return (
          <Tooltip title={enderecoCompleto} placement="topLeft">
            <div className={styles.addressContainer}>
              <EnvironmentOutlined />
              <span className={styles.truncatedText}>{enderecoCompleto}</span>
            </div>
          </Tooltip>
        )
      },
    },
    {
      title: "Descrição",
      dataIndex: "assuntoConsulta",
      key: "assuntoConsulta",
      width: 200,
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          <span className={styles.truncatedText}>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Ações",
      key: "acoes",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <Space>
          {record.urlDocumentos && record.urlDocumentos.length > 0 && (
            <Tooltip title="Visualizar Documento">
              <Button
                type="primary"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handlePreview(`https://credx-bucket.s3.amazonaws.com/${record.urlDocumentos[0]}`)}
                className={styles.actionButton}
              />
            </Tooltip>
          )}
          <Tooltip title="Download PDF">
            <Button
              type="default"
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleDownloadClick(record)}
              className={styles.actionButton}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  return (
    <div className={styles.pageContainer}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerTop}>
          <Breadcrumb
            items={[
              {
                href: "",
                title: <HomeOutlined />,
              },
              {
                href: "",
                title: <span>Agendamentos</span>,
              },
              {
                title: "Consultas",
              },
            ]}
            className={styles.breadcrumb}
          />
        </div>

        <div className={styles.headerContent}>
          <div className={styles.titleSection}>
            <Title level={2} className={styles.pageTitle}>
              <FileTextOutlined /> Consultas
            </Title>
            <Text className={styles.pageDescription}>Gerencie e acompanhe todas as consultas dos clientes</Text>
          </div>

          <div className={styles.statsCards}>
            <Card className={styles.statCard}>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>{filteredClientes.length}</div>
                <div className={styles.statLabel}>Total</div>
              </div>
            </Card>
            <Card className={styles.statCard}>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>{filteredClientes.filter((c) => c.etapa === "contato").length}</div>
                <div className={styles.statLabel}>Contato</div>
              </div>
            </Card>
            <Card className={styles.statCard}>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>
                  {filteredClientes.filter((c) => c.etapa === "negociacao").length}
                </div>
                <div className={styles.statLabel}>Negociação</div>
              </div>
            </Card>
            <Card className={styles.statCard}>
              <div className={styles.statContent}>
                <div className={styles.statNumber}>{filteredClientes.filter((c) => c.etapa === "fechado").length}</div>
                <div className={styles.statLabel}>Fechado</div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className={styles.filtersCard}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Buscar por nome, email, telefone ou CPF/CNPJ"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              className={styles.searchInput}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Filtrar por etapa"
              value={filterEtapa}
              onChange={setFilterEtapa}
              style={{ width: "100%" }}
              className={styles.filterSelect}
            >
              <Option value="all">Todas as etapas</Option>
              <Option value="contato">Contato</Option>
              <Option value="negociacao">Negociação</Option>
              <Option value="fechado">Fechado</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Tipo de atendimento"
              value={filterPresencial}
              onChange={setFilterPresencial}
              style={{ width: "100%" }}
              className={styles.filterSelect}
            >
              <Option value="all">Todos</Option>
              <Option value="presencial">Presencial</Option>
              <Option value="online">Online</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <Button icon={<FilterOutlined />} onClick={clearFilters}>
                Limpar Filtros
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
                Atualizar
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card className={styles.tableCard}>
        <Spin spinning={loading}>
          <Table
            dataSource={filteredClientes}
            columns={columns}
            rowKey="id"
            scroll={{ x: 1400 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
            }}
            className={styles.consultasTable}
            locale={{
              emptyText: <Empty description="Nenhuma consulta encontrada" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
            }}
          />
        </Spin>
      </Card>

      {/* Modals */}
      <Modal
        visible={isModalOpen}
        title="Visualizar Documento"
        footer={null}
        onCancel={() => setIsModalOpen(false)}
        width={800}
        className={styles.previewModal}
      >
        {previewFileUrl && <img alt="documento" style={{ width: "100%" }} src={previewFileUrl || "/placeholder.svg"} />}
      </Modal>

      <Modal
        visible={secondModalOpen}
        title={
          <div className={styles.modalHeader}>
            <UserOutlined />
            <span>Resumo das Informações</span>
          </div>
        }
        onCancel={() => setSecondModalOpen(false)}
        width={900}
        footer={[
          <Button key="back" onClick={() => setSecondModalOpen(false)}>
            Fechar
          </Button>,
          <Button key="generatePDF" type="primary" icon={<DownloadOutlined />} onClick={generatePDF}>
            Gerar PDF
          </Button>,
        ]}
        className={styles.detailModal}
      >
        {selectedCliente && (
          <div id="second-modal-content" className={styles.modalContent}>
            <div className={styles.clientHeader}>
              <div className={styles.clientAvatar}>
                <UserOutlined />
              </div>
              <div className={styles.clientMainInfo}>
                <h3>{selectedCliente.nomeCliente}</h3>
                <Tag color={getEtapaColor(selectedCliente.etapa)}>{getEtapaText(selectedCliente.etapa)}</Tag>
              </div>
            </div>

            <Divider />

            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>CPF/CNPJ:</label>
                  <Input value={selectedCliente.cpfCnpj} readOnly />
                </div>
              </Col>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Email:</label>
                  <Input value={selectedCliente.email} readOnly prefix={<MailOutlined />} />
                </div>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Telefone:</label>
                  <Input value={selectedCliente.telefone} readOnly prefix={<PhoneOutlined />} />
                </div>
              </Col>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Atendimento:</label>
                  <Input value={selectedCliente.isPresencial ? "Presencial" : "Online"} readOnly />
                </div>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Data de Nascimento:</label>
                  <Input
                    value={selectedCliente.dataNascimento ? formatIfValid(selectedCliente.dataNascimento) : ""}
                    readOnly
                    prefix={<CalendarOutlined />}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Data da Consulta:</label>
                  <Input
                    value={selectedCliente.dataConsulta ? formatIfValid(selectedCliente.dataConsulta) : ""}
                    readOnly
                    prefix={<CalendarOutlined />}
                  />
                </div>
              </Col>
            </Row>

            <Divider orientation="left">Endereço</Divider>

            <Row gutter={[24, 16]}>
              <Col span={14}>
                <div className={styles.infoGroup}>
                  <label>Logradouro:</label>
                  <Input value={selectedCliente.logradouro} readOnly />
                </div>
              </Col>
              <Col span={5}>
                <div className={styles.infoGroup}>
                  <label>Número:</label>
                  <Input value={selectedCliente.numero} readOnly />
                </div>
              </Col>
              <Col span={5}>
                <div className={styles.infoGroup}>
                  <label>CEP:</label>
                  <Input value={selectedCliente.cep} readOnly />
                </div>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Bairro:</label>
                  <Input value={selectedCliente.bairro} readOnly />
                </div>
              </Col>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Cidade/UF:</label>
                  <Input value={`${selectedCliente.cidade}/${selectedCliente.estado}`} readOnly />
                </div>
              </Col>
            </Row>

            <Divider orientation="left">Detalhes da Consulta</Divider>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <div className={styles.infoGroup}>
                  <label>Assunto:</label>
                  <Input.TextArea value={selectedCliente.assuntoConsulta} readOnly rows={3} />
                </div>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Gravar Consulta:</label>
                  <Input value={selectedCliente.gravarConsulta ? "Sim" : "Não"} readOnly />
                </div>
              </Col>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Documentos:</label>
                  <Input value={selectedCliente.documentosRelacionados?.length > 0 ? "Sim" : "Não"} readOnly />
                </div>
              </Col>
            </Row>

            <Divider orientation="left">Preferências</Divider>

            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Ar-condicionado:</label>
                  <Input value={selectedCliente.arCondicionadoLigado ? "Sim" : "Não"} readOnly />
                </div>
              </Col>
              {selectedCliente.arCondicionadoLigado && (
                <Col span={12}>
                  <div className={styles.infoGroup}>
                    <label>Temperatura:</label>
                    <Input value={selectedCliente.temperaturaAr} readOnly />
                  </div>
                </Col>
              )}
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Água:</label>
                  <Input value={selectedCliente.aguaPreferencia ?? ""} readOnly />
                </div>
              </Col>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Café:</label>
                  <Input value={selectedCliente.cafe ? "Sim" : "Não"} readOnly />
                </div>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Refrigerante:</label>
                  <Input value={selectedCliente.refrigerantePreferencia ?? ""} readOnly />
                </div>
              </Col>
              <Col span={12}>
                <div className={styles.infoGroup}>
                  <label>Suco/Chá:</label>
                  <Input value={selectedCliente.sucoOuChasPreferencia ?? ""} readOnly />
                </div>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <div className={styles.infoGroup}>
                  <label>Comida Preferida:</label>
                  <Input value={selectedCliente.comidaPreferencia ?? ""} readOnly />
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default Consultas
